import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Sparkles, ArrowRight } from 'lucide-react';
import { FloatingDoodles } from '../components/Doodles';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFAF0] flex items-center justify-center p-4 relative">
      <FloatingDoodles />
      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FFC8DD] border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] mb-4">
            <Heart className="w-10 h-10 text-black" strokeWidth={2.5} />
          </div>
          <h1 className="text-4xl font-bold font-['Fredoka'] text-gray-900">Focus Critter</h1>
          <p className="text-gray-600 mt-2 font-['Nunito']">Your cute ADHD companion awaits!</p>
        </div>

        {/* Login Card */}
        <div className="neo-card p-8">
          <h2 className="text-2xl font-bold font-['Fredoka'] mb-6 text-center">Welcome Back!</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-[#FF6B6B] text-white rounded-lg border-2 border-black font-semibold" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="neo-input w-full"
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="neo-input w-full"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="neo-btn w-full py-3 bg-[#A2D2FF] text-black flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <span>Let&apos;s Go!</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">eller</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <GoogleSignInButton label="Fortsätt med Google" testId="login-google-btn" />

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-gray-500 text-sm hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-600 font-['Nunito']">
              New here?{' '}
              <Link to="/register" className="text-[#A2D2FF] font-bold hover:underline" data-testid="register-link">
                Create an account
              </Link>
            </p>
          </div>
        </div>

        {/* Fun decoration */}
        <div className="flex justify-center gap-4 mt-8">
          <Sparkles className="w-6 h-6 text-[#FFC8DD]" />
          <Sparkles className="w-6 h-6 text-[#B9FBC0]" />
          <Sparkles className="w-6 h-6 text-[#CDB4DB]" />
        </div>
      </div>
    </div>
  );
}
